import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchAuthSession } from "aws-amplify/auth";
import { Container, Row, Col, Card } from "react-bootstrap";

import MessageIcon from "@/assets/message.svg";
import MessageIconHover from "@/assets/message-hover.svg";
import NotesIcon from "@/assets/notes.svg";
import NotesIconHover from "@/assets/notes-hover.svg";
import ArrowRight from "@/assets/arrow-right.svg";

import styles from "./Hero.module.css";

const cardsData = [
  {
    key: "initiate" as const,
    title: "Initiate a Session",
    desc: "For me and another person",
    icon: MessageIcon,
    iconHover: MessageIconHover,
  },
  {
    key: "open" as const,
    title: "Open My Session",
    desc: "Continue conversations",
    icon: NotesIcon,
    iconHover: NotesIconHover,
  },
];

function clearResumeStateFromLocalStorage() {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i) ?? "";
      if (!key) continue;
      if (key === "accordia:lastIssueId") keysToRemove.push(key);
      if (key.startsWith("accordia:propose:")) keysToRemove.push(key);
      if (key.startsWith("solutionsSaved:")) keysToRemove.push(key);
      if (key.startsWith("accordia:agree:")) keysToRemove.push(key);
    }
    keysToRemove.forEach((k) => localStorage.removeItem(k));
  } catch {
    // ignore
  }
}

export function Hero() {
  const [hoveredCard, setHoveredCard] = useState<number | null>(null);
  const navigate = useNavigate();

  const goToLoginWithNext = (next: string) => {
    navigate(`/login?next=${encodeURIComponent(next)}`);
  };

  const handleInitiateClick = async () => {
    clearResumeStateFromLocalStorage();
    try {
      const session = await fetchAuthSession();
      const isSignedIn =
        !!session?.tokens?.idToken || !!session?.tokens?.accessToken;
      if (isSignedIn) navigate("/init");
      else goToLoginWithNext("/init");
    } catch {
      goToLoginWithNext("/init");
    }
  };

  const handleOpenSessionClick = async () => {
    try {
      const session = await fetchAuthSession();
      const isSignedIn =
        !!session?.tokens?.idToken || !!session?.tokens?.accessToken;
      if (isSignedIn) navigate("/session");
      else goToLoginWithNext("/session");
    } catch {
      goToLoginWithNext("/session");
    }
  };

  return (
    <section className={styles.heroSection}>
      <Container>
        <Row className={`${styles.heroGrid} align-items-center`}>
          {/* Full-width column on desktop so cards can be wide */}
          <Col xs={12} lg={12} className={styles.leftColumn}>
            
            {/* --- START AWS BADGE SECTION --- */}
            {/* d-flex keeps logo and text in a row. align-items-center centers them vertically. */}
            <div className="d-flex align-items-center mb-4">
              <a 
                href="https://aws.amazon.com/what-is-cloud-computing" 
                target="_blank" 
                rel="noopener noreferrer"
                className="me-3" // Adds spacing between logo and text
              >
                <img 
                  src="https://d0.awsstatic.com/logos/powered-by-aws.png" 
                  alt="Powered by AWS Cloud Computing"
                  style={{ height: "40px", width: "auto" }} 
                />
              </a>
              <span className="text-secondary fw-bold" style={{ fontSize: '0.9rem' }}>
                Awarded AWS Activate Startup Credits
              </span>
            </div>
            {/* --- END AWS BADGE SECTION --- */}

            <div className={styles.headingBlock}>
              <h1 className={styles.heroTitle}>Resolve. Repair. Reconnect.</h1>
              <p className={styles.heroSubtitle}>
                Turn your conflicts into breakthroughs.
              </p>
            </div>

            {/* Cards row */}
            <Row className={`${styles.cardsRow} justify-content-center`}>
              {cardsData.map((card, index) => {
                const onPress =
                  card.key === "initiate"
                    ? handleInitiateClick
                    : handleOpenSessionClick;

                const cardColorClass =
                  card.key === "initiate"
                    ? styles.cardInitiate
                    : styles.cardOpen;

                return (
                  <Col key={card.key} xs={12} md={6} className="mb-3 d-flex">
                    <Card
                      role="button"
                      tabIndex={0}
                      aria-label={card.title}
                      onClick={(e: React.MouseEvent) => {
                        e.stopPropagation();
                        onPress();
                      }}
                      onKeyDown={(e: React.KeyboardEvent) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          e.stopPropagation();
                          onPress();
                        }
                      }}
                      onMouseEnter={() => setHoveredCard(index)}
                      onMouseLeave={() => setHoveredCard(null)}
                      className={`${styles.card} ${cardColorClass} w-100 h-100`}
                    >
                      <Card.Body className={styles.cardBody}>
                        <div className={styles.cardHeader}>
                          <div className={styles.iconWrapper}>
                            <img
                              src={card.icon}
                              alt=""
                              className={styles.iconImg}
                              style={{
                                opacity: hoveredCard === index ? 0 : 1,
                              }}
                            />
                            <img
                              src={card.iconHover}
                              alt=""
                              className={styles.iconImg}
                              style={{
                                opacity: hoveredCard === index ? 1 : 0,
                              }}
                            />
                          </div>
                          <h3 className={styles.cardTitle}>
                            {card.title}
                            <img
                              src={ArrowRight}
                              alt=""
                              className={styles.cardArrow}
                            />
                          </h3>
                        </div>
                        <p className={styles.cardDesc}>{card.desc}</p>
                      </Card.Body>
                    </Card>
                  </Col>
                );
              })}
            </Row>
          </Col>
        </Row>
      </Container>
    </section>
  );
}